import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import { isPlatform } from '@ionic/react';
import { AllieText } from '../Text/Text';
import { MobileTabButton } from './AllieMobileMenu.styles';

// Constants for portal containers
const MOBILE_TABS_CONTAINER = 'mobile-tabs-container';
const MOBILE_TABS_CONTENT = 'mobile-tabs-content';

// Reference counting system for automatic visibility management
let tabsReferenceCount = 0;

const updateTabsVisibility = () => {
  const mainTabsContainer = document.getElementById(MOBILE_TABS_CONTAINER);
  if (mainTabsContainer) {
    const shouldShow = tabsReferenceCount > 0;
    const isExplicitlyHidden = mainTabsContainer.dataset.controllerHidden === 'true';

    mainTabsContainer.style.display = shouldShow && !isExplicitlyHidden ? 'block' : 'none';
  }
};

const addTabsReference = () => {
  tabsReferenceCount++;
  if (process.env.NODE_ENV === 'development') {
    console.log(`[AllieMobileTabs] Reference added. Count: ${tabsReferenceCount}`);
  }
  updateTabsVisibility();
};

const removeTabsReference = () => {
  tabsReferenceCount = Math.max(0, tabsReferenceCount - 1);
  if (process.env.NODE_ENV === 'development') {
    console.log(`[AllieMobileTabs] Reference removed. Count: ${tabsReferenceCount}`);
  }
  updateTabsVisibility();
};

export interface MobileTabItem {
  id: string;
  label?: string;
  href: string;
  showNotificationBadge?: boolean;
  icon: React.ReactNode;
}

export interface AllieMobileAppTabsProps {
  tabs: MobileTabItem[];
  selectedTab?: string;
  className?: string;
  style?: React.CSSProperties;
  onTabClick?: (tabId: MobileTabItem) => void;
  tabContainerComponent?: React.ComponentType<{
    isSelected: boolean;
    children: React.ReactNode;
  }>;
  showTabs?: boolean; // Control visibility of tabs
  // New props for styling flexibility
  variant?: 'default' | 'flat' | 'minimal'; // Predefined variants
  showBorderRadius?: boolean; // Control border radius
  showBoxShadow?: boolean; // Control shadow
  customBorderRadius?: string; // Custom border radius
  tabBarHeight?: string; // Control tab bar height (default: '60px')
  tabBarStyle?: React.CSSProperties; // Override tab bar inner styles
}

/**
 * AllieMobileAppTabs - Mobile app tabs component using Ionic
 * Configurable through props to avoid coupling with specific implementations
 */
export const AllieMobileAppTabs: FC<AllieMobileAppTabsProps> = ({
  tabs,
  selectedTab,
  className,
  style,
  onTabClick,
  tabContainerComponent: TabContainer,
  showTabs = true,
  variant = 'default',
  showBorderRadius = true,
  showBoxShadow = true,
  customBorderRadius,
  tabBarHeight = '60px',
  tabBarStyle,
}) => {
  // Add reference when component mounts and showTabs is true
  React.useEffect(() => {
    if (showTabs) {
      addTabsReference();
      return () => removeTabsReference();
    }
    return undefined;
  }, [showTabs]);

  // Function to get base styles according to variant
  const getVariantStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: 'fixed',
      bottom: 0,
      width: '100%',
      overflow: 'hidden',
      backgroundColor: 'var(--allie-color-light-main)',
      ...(isPlatform('android') && { paddingBottom: 'env(safe-area-inset-bottom)' }),
    };

    switch (variant) {
      case 'flat':
        return {
          ...baseStyles,
          borderRadius: 0,
          boxShadow: 'none',
          borderTop: '1px solid var(--allie-color-neutral-200)',
        };

      case 'minimal':
        return {
          ...baseStyles,
          borderRadius: 0,
          boxShadow: 'none',
          backgroundColor: 'transparent',
        };

      case 'default':
      default:
        return {
          ...baseStyles,
          borderRadius: showBorderRadius
            ? customBorderRadius || 'var(--allie-radius-3xl) var(--allie-radius-3xl) 0 0'
            : 0,
          boxShadow: showBoxShadow ? '0 -12px 16px -4px rgba(16, 24, 40, 0.08)' : 'none',
        };
    }
  };

  const defaultStyle: React.CSSProperties = {
    ...getVariantStyles(),
    ...style, // Custom styles have priority
  };

  const defaultTabBarStyle = {
    height: tabBarHeight,
    justifyContent: 'space-around' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    position: 'relative' as const,
    backgroundColor: 'var(--allie-color-light-main)',
    ...tabBarStyle, // Custom tab bar styles have priority
  };

  // Don't render anything if showTabs is false
  if (!showTabs) {
    return null;
  }

  return (
    <div id={MOBILE_TABS_CONTAINER} className={className} style={defaultStyle}>
      {/* Default tabs navigation */}
      <div id="default-tabs-navigation">
        <div slot="bottom" style={defaultTabBarStyle}>
          {tabs.map((tab) => {
            const isSelected = selectedTab === tab.id;

            return (
              <MobileTabButton
                key={tab.id}
                fill="clear"
                onClick={(e: React.MouseEvent<HTMLIonButtonElement>) => {
                  e.preventDefault();
                  onTabClick?.(tab);
                }}
              >
                {TabContainer ? (
                  <TabContainer isSelected={isSelected}>
                    <div style={{ position: 'relative' }}>
                      {tab.icon}
                      {tab.showNotificationBadge && (
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            backgroundColor: 'var(--allie-color-error-60)',
                            borderRadius: '50%',
                            border: '2px solid var(--allie-color-white)',
                            position: 'absolute',
                            top: '-4px',
                            right: '0',
                          }}
                        ></div>
                      )}
                      <AllieText
                        type="button-small"
                        style={{ textTransform: 'capitalize' }}
                        weight={isSelected ? 600 : 500}
                      >
                        {tab.label}
                      </AllieText>
                    </div>
                  </TabContainer>
                ) : (
                  <>
                    {tab.icon}
                    {tab.label && (
                      <AllieText type="button-small" weight={isSelected ? 600 : 500}>
                        {tab.label}
                      </AllieText>
                    )}
                  </>
                )}
              </MobileTabButton>
            );
          })}
        </div>
      </div>

      {/* Portal container for custom content - replaces tabs when content is added */}
      <div
        id={MOBILE_TABS_CONTENT}
        style={{
          ...defaultTabBarStyle,
          display: 'none',
        }}
      />
    </div>
  );
};

AllieMobileAppTabs.displayName = 'AllieMobileAppTabs';

/**
 * AllieMobileTabsController - Controls the visibility of mobile tabs
 * Similar to AllieHeaderTitle but for mobile tabs visibility
 */
export const AllieMobileTabsController: FC<{
  showTabs: boolean;
  children?: React.ReactNode;
}> = ({ showTabs, children }) => {
  React.useEffect(() => {
    const tabsContainer = document.getElementById(MOBILE_TABS_CONTAINER);
    if (tabsContainer) {
      // Mark if explicitly hidden by controller
      tabsContainer.dataset.controllerHidden = (!showTabs).toString();
      updateTabsVisibility();
    }

    // Cleanup function to remove controller hidden mark when component unmounts
    return () => {
      const cleanupContainer = document.getElementById(MOBILE_TABS_CONTAINER);
      if (cleanupContainer) {
        delete cleanupContainer.dataset.controllerHidden;
        updateTabsVisibility();
      }
    };
  }, [showTabs]);

  return <>{children}</>;
};

/**
 * AllieMobileTabsContent - Portal content to mobile tabs
 * When content is provided, hides default tabs and shows custom content
 * When no content, shows default tabs navigation
 */
export const AllieMobileTabsContent: FC<{
  children: React.ReactNode;
  // Props to style the main container (the one with borderRadius and boxShadow)
  containerStyle?: React.CSSProperties;
  containerClassName?: string;
  // Props to style the internal content container
  contentStyle?: React.CSSProperties;
  contentClassName?: string;
  // Props for quick variants
  variant?: 'default' | 'flat' | 'minimal' | 'transparent';
  // Specific props to control common styles
  showBorderRadius?: boolean;
  showBoxShadow?: boolean;
  showBorder?: boolean;
  contentHeight?: string; // Control content height (default: '90px')
}> = ({
  children,
  containerStyle,
  containerClassName,
  contentStyle,
  contentClassName,
  variant = 'default',
  showBorderRadius = true,
  showBoxShadow = true,
  showBorder = true,
  contentHeight = '90px',
}) => {
  const [container, setContainer] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    // Add reference for this content component
    addTabsReference();

    const tabsContainer = document.getElementById(MOBILE_TABS_CONTENT);
    const defaultNavigation = document.getElementById('default-tabs-navigation');
    const mainTabsContainer = document.getElementById(MOBILE_TABS_CONTAINER);

    if (tabsContainer) {
      // Apply styles to main container
      if (mainTabsContainer) {
        // Apply styles according to variant
        const getContainerStyles = () => {
          const baseStyles = {
            position: 'fixed',
            bottom: '0',
            width: '100%',
            overflow: 'hidden',
          };

          switch (variant) {
            case 'flat':
              return {
                ...baseStyles,
                borderRadius: '0',
                boxShadow: 'none',
                borderTop: '1px solid var(--allie-color-neutral-200)',
              };

            case 'minimal':
              return {
                ...baseStyles,
                borderRadius: '0',
                boxShadow: 'none',
                backgroundColor: 'transparent',
              };

            case 'transparent':
              return {
                ...baseStyles,
                borderRadius: '0',
                boxShadow: 'none',
                backgroundColor: 'transparent',
                border: 'none',
              };

            case 'default':
            default:
              return {
                ...baseStyles,
                borderRadius: showBorderRadius
                  ? 'var(--allie-radius-3xl) var(--allie-radius-3xl) 0 0'
                  : '0',
                boxShadow: showBoxShadow ? '0 -12px 16px -4px rgba(16, 24, 40, 0.08)' : 'none',
              };
          }
        };

        const styles = getContainerStyles();

        Object.assign(mainTabsContainer.style, styles, containerStyle || {});

        if (containerClassName) {
          mainTabsContainer.className = containerClassName;
        }
      }

      // Clear any previous content in the container
      if (!tabsContainer.dataset.mounted) {
        tabsContainer.innerHTML = '';
        tabsContainer.dataset.mounted = 'true';
        setContainer(tabsContainer);

        // Apply styles to content container
        const getContentStyles = () => {
          const baseStyles = {
            height: contentHeight,
            justifyContent: 'space-around',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            backgroundColor: 'var(--allie-color-light-main)',
          };

          if (variant === 'transparent') {
            return {
              ...baseStyles,
              backgroundColor: 'transparent',
              borderTop: 'none',
            };
          }

          return {
            ...baseStyles,
            borderTop: showBorder
              ? '0.55px solid var(--border, var(--ion-color-step-150, rgba(0,0,0,0.2)))'
              : 'none',
          };
        };

        const contentStyles = getContentStyles();

        // Merge styles with custom contentStyle having priority
        Object.assign(tabsContainer.style, contentStyles, contentStyle || {});

        if (contentClassName) {
          tabsContainer.className = `ion-tab-bar ${contentClassName}`;
        }

        // Show custom container and hide default navigation
        tabsContainer.style.display = 'flex';
        if (defaultNavigation) {
          defaultNavigation.style.display = 'none';
        }
      }
    }

    return () => {
      // Remove reference for this content component
      removeTabsReference();

      if (tabsContainer) {
        delete tabsContainer.dataset.mounted;
        tabsContainer.innerHTML = '';
        tabsContainer.style.display = 'none';
        if (defaultNavigation) {
          defaultNavigation.style.display = 'block';
        }
      }
    };
  }, [
    containerStyle,
    containerClassName,
    contentStyle,
    contentClassName,
    variant,
    showBorderRadius,
    showBoxShadow,
    showBorder,
    contentHeight,
  ]);

  // If no container exists, render nothing
  if (!container) return null;

  // Portal content to the tabs container
  return ReactDOM.createPortal(children, container);
};
